'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CategoryForm, type CategoryFormRef } from '@/components/partials/categories'
import { ContentError, FormSkeleton } from '@/components/partials'
import { UpdateCategoryParams } from '@/types/category'
import { useCategory, useUpdateCategory } from '@/hooks'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const categoryFormRef = useRef<CategoryFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: category, isLoading, error } = useCategory(id)
  const { mutate: updateCategory, isPending } = useUpdateCategory()

  const handleSubmit = (data: UpdateCategoryParams) => {
    updateCategory(
      { categoryId: id, params: data },
      {
        onSuccess: async updatedCategory => {
          setIsRedirecting(true)
          queryClient.setQueryData(['category', id], updatedCategory)
          await queryClient.invalidateQueries({ queryKey: ['categories'] })
          await queryClient.invalidateQueries({ queryKey: ['categories-tree'] })
          toast.success(t('updated_successfully'))
          router.push(`/products/categories/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (categoryFormRef.current) {
        categoryFormRef.current.handleValidationErrors(error)
      }
      return
    }

    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/products/categories/${id}`)
  }

  if (isLoading || !category) {
    return <FormSkeleton fieldRows={2} />
  }

  if (error || !category) {
    return <ContentError error={error} listUrl="/products/categories" />
  }

  return (
    <CategoryForm
      ref={categoryFormRef}
      mode={FormModeEnum.UPDATE}
      category={category}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
